package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.ApiErrorBody
import dev.lingvo.shared.CreateDesignSnapshotRequest
import dev.lingvo.shared.DesignSnapshotDto
import dev.lingvo.shared.RestoreDesignRequest
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.UpdateDesignSnapshotRequest
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import dev.lingvo.site.i18n.t

/** Keeps the editor transport scoped; the response bound includes a 256 KiB design plus restore metadata. */
internal object DesignSnapshotApi {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    suspend fun create(slug: String, platform: StorePlatform, request: CreateDesignSnapshotRequest): DesignSnapshotDto =
        json.decodeFromString(send("POST", base(slug, platform), json.encodeToString(request)))
    suspend fun update(slug: String, platform: StorePlatform, version: Int, request: UpdateDesignSnapshotRequest): DesignSnapshotDto =
        json.decodeFromString(send("PUT", "${base(slug, platform)}/$version", json.encodeToString(request)))
    suspend fun delete(slug: String, platform: StorePlatform, version: Int, revision: Int) {
        send("DELETE", "${base(slug, platform)}/$version?revision=$revision")
    }
    suspend fun restore(slug: String, platform: StorePlatform, version: Int, baseVersion: Int): ScreenshotDesignDto =
        json.decodeFromString(send("POST", "${base(slug, platform)}/$version/restore", json.encodeToString(RestoreDesignRequest(baseVersion))))

    private fun base(slug: String, platform: StorePlatform) = "/projects/${encodeURIComponent(slug)}/screenshots/${platform.id}/design/snapshots"

    private suspend fun send(method: String, path: String, body: String? = null): String {
        val controller: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
        try {
            return withTimeout(20_000) {
                val headers = Headers().apply {
                    set("Accept", "application/json")
                    if (body != null) set("Content-Type", "application/json")
                    Session.token?.takeIf { it.isNotBlank() }?.let { set("Authorization", "Bearer $it") }
                }
                val init = RequestInit(method = method, headers = headers, body = body)
                if (controller != null) init.asDynamic().signal = controller.signal
                val response = window.fetch("${ApiConfig.baseUrl}$path", init).await()
                val raw = response.text().await()
                if (raw.length > 524288) throw ApiException(502, "invalid_response", tr("app.projects.slug.screenshots.design_snapshot_api.snapshot_response_exceeded_its_limit", "Snapshot response exceeded its limit."))
                if (!response.ok) {
                    val error = runCatching { json.decodeFromString<ApiErrorBody>(raw).error }.getOrNull()
                    throw ApiException(response.status.toInt(), error?.code ?: "snapshot_request_failed",
                        error?.message ?: tr("app.projects.slug.screenshots.design_snapshot_api.the_snapshot_request_failed", "The snapshot request failed."), error?.details ?: kotlinx.serialization.json.JsonObject(emptyMap()))
                }
                raw
            }
        } catch (_: TimeoutCancellationException) { throw ApiException(0, "request_timeout", tr("app.projects.slug.screenshots.design_snapshot_api.the_snapshot_request_failed_retry", "The snapshot request failed. Retry.")) }
        catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: ApiException) { throw failure }
        catch (_: Throwable) { throw ApiException(0, "snapshot_request_failed", tr("app.projects.slug.screenshots.design_snapshot_api.the_snapshot_request_failed_retry", "The snapshot request failed. Retry.")) }
        finally { controller?.abort() }
    }
}

private external fun encodeURIComponent(value: String): String
